import moment from 'moment';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Button, Card, Dropdown, Icon, Modal } from 'rs-emd-ui-atoms';
import { clickEvent } from '../../../../../tagging/ensighten';
import { getLabel } from '../../../helpers/html.utils';
import { DocumentType, IDocumentItem } from '../../../models/common/document-item.model';
import { ILabel } from '../../../models/common/label.model';
import { IPDPCADData } from '../../../models/pdp/cad-documents/pdp-cad-data.model';
import { IPDPDocuments } from '../../../models/pdp/pdp-documents.model';
import { IPDPTitle } from '../../../models/pdp/pdp-title.model';
import ROHSLogo from './../../../images/rohs-logo.png';
import { ReactComponent as Logo } from './../../../images/rs-logo.svg';
import styles from './technical-documents-component.module.scss';

type TechnicalDocumentsProps = {
  labels?: ILabel[] | null;
  documentsArea: IPDPDocuments;
  titleArea?: IPDPTitle | null;
  cadDocuments?: IPDPCADData;
  downloadCADDocument: (documentId: number) => void;
};

export const TechnicalDocuments: FunctionComponent<TechnicalDocumentsProps> = (props: TechnicalDocumentsProps) => {
  const [showDocModal, setShowDocModal] = useState<IDocumentItem | undefined>(undefined);
  const [selectedCADDocument, setSelectedCADDocument] = useState<number | undefined>(undefined);
  const [isCADDownloadEnabled, setIsCADDownloadEnabled] = useState<boolean>(false);
  const [cadDocuments, setCadDocuments] = useState<IPDPCADData>();

  const componentRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current ?? null,
  });

  useEffect(() => {
    setCadDocuments(props.cadDocuments);
  }, [props.cadDocuments]);

  return (
    <Card className={styles['documents-container']} data-verbolia-id='technical-documents-container'>
      <p className={styles.title}>{getLabel(props.labels, 'technical_documents')}</p>

      <div className={styles.documents}>
        {props.documentsArea.documents.map((doc) => {
          return (
            <div className={styles['doc-wrapper']} key={doc.title}>
              <div className={styles['doc-container']}>
                <div
                  className={`${styles.icon} ${
                    doc.documentType === DocumentType.TraceParts3DCad && doc.url !== '/user/login' ? styles['three-d-icon'] : ''
                  }`}>
                  <Icon name={getIcon(doc.documentType)} />
                </div>

                <div className={styles['document']}>
                  {/* documents dropdown */}
                  {doc.documentType === DocumentType.TraceParts3DCad &&
                    props.cadDocuments &&
                    props.cadDocuments.tracePartsData.documents.length > 0 && (
                      <Dropdown
                        background='grey'
                        className={styles['dd-cad']}
                        defaultSelection={props.cadDocuments.tracePartsData.documents[0].id}
                        list={props.cadDocuments.tracePartsData.documents.map((cd) => {
                          return { key: cd.id, elt: <>{cd.name}</> };
                        })}
                        selectCallback={(key) => setSelectedCADDocument(key as number)}></Dropdown>
                    )}
                  {/* download/view links */}
                  <div className={styles.links}>
                    {doc.documentType !== DocumentType.TraceParts3DCad && (
                      <Button
                        buttonType='text'
                        text={doc.title}
                        className={styles.link}
                        href={isPdf(doc.documentType) ? doc.url : undefined}
                        isExternalLink={true}
                        target='_blank'
                        onClick={!isPdf(doc.documentType) ? () => setShowDocModal(doc) : () => documentClickEvent(doc.documentType)}
                      />
                    )}
                    {doc.documentType === DocumentType.TraceParts3DCad && getTraceParts3DCadBtn(doc)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showDocModal && openDocModal(showDocModal)}
    </Card>
  );

  function getTraceParts3DCadBtn(doc: IDocumentItem) {
    return (
      <Button
        buttonType='text'
        text={doc.url === 'cadDocuments' ? getLabel(props.labels, 'download') : doc.urlText ?? ''}
        isExternalLink={true}
        className={`${styles.link} ${doc.url === '/user/login' ? '' : styles['link-download']}`}
        disabled={(cadDocuments === undefined || cadDocuments?.tracePartsData.documents.length === 0) && doc.url === 'cadDocuments'}
        onClick={() => {
          if (doc.url !== 'cadDocuments') {
            getTraceParts3DCadBtnUrl(doc.url);
          } else {
            setShowDocModal(doc);
          }
        }}
      />
    );
  }

  function getTraceParts3DCadBtnUrl(url: string) {
    if (url === '/user/login') {
      window.location.href = `/user/login?redirectTo=${window.location.pathname}`;
    } else {
      window.location.href = url;
    }
  }

  function isPdf(docType: DocumentType): boolean {
    return docType === DocumentType.Pdf || docType === DocumentType.ElrProductInformationSheet;
  }

  function getIcon(docType: DocumentType) {
    if (isPdf(docType)) {
      return 'pdf';
    } else if (docType === DocumentType.TraceParts3DCad) {
      return '3d';
    } else {
      return 'document';
    }
  }

  function documentClickEvent(docType: DocumentType) {
    clickEvent({
      __Info:
        docType === DocumentType.ConformityStatement
          ? 'COC_LINK_PDP'
          : docType === DocumentType.RohsCompliance
          ? 'ROHS_LINK_PDP'
          : 'DATASHEET_LINK_PDP',
      __Result: props.titleArea?.stockCode,
    });
  }

  function openDocModal(doc: IDocumentItem) {
    if (doc.documentType !== DocumentType.TraceParts3DCad) {
      documentClickEvent(doc.documentType);
      return getDocumentModal(doc);
    } else {
      return get3DCadModal();
    }
  }

  function getDocumentModal(doc: IDocumentItem) {
    return (
      <Modal
        headerElt={getModalHeader(doc)}
        mobileFooterElt={getModalFooter()}
        closeCallback={() => setShowDocModal(undefined)}
        className={styles.modal}
        isMobileFullScreen={true}>
        <div
          ref={componentRef}
          className={`${styles['modal-document']} ${
            doc.documentType === DocumentType.ConformityStatement.valueOf() ? styles['conformity-statement'] : styles['rohs-compliance']
          }`}>
          <div className='print-only'>{getModalHeader(doc)}</div>
          <div className={styles.content}>
            {doc.documentType === DocumentType.ConformityStatement.valueOf() ? getStatementOfConformityContent() : getROHSContent()}
          </div>
        </div>
      </Modal>
    );
  }

  function get3DCadModal() {
    return (
      <Modal
        headerElt={getLabel(props.labels, '3d_cad_tos_title')}
        footerElt={getCADModalFooter()}
        closeCallback={() => setShowDocModal(undefined)}
        scrolledToBottom={() => {
          setIsCADDownloadEnabled(true);
        }}
        className={`${styles.modal} ${styles.tos}`}
        isSidebar={true}>
        <div className={styles['cad-tos']}>
          <p>{getLabel(props.labels, '3d_cad_tos_content')}</p>
        </div>
        <div className={!isCADDownloadEnabled ? styles['fade-out'] : ''}></div>
      </Modal>
    );
  }

  function getModalHeader(doc: IDocumentItem) {
    return (
      <div className={styles['modal-header']}>
        <div className={styles['modal-title']}>
          <Logo />
          <h2 className={styles['heading']}>
            {getLabel(props.labels, 'ROHScert_Header_RSComponents')} <span>{doc.title}</span>
          </h2>
        </div>
        <Button
          icon='printer'
          text={getLabel(props.labels, 'print')}
          buttonType='text'
          className={styles.print}
          onClick={handlePrint}></Button>
      </div>
    );
  }

  function getModalFooter() {
    return (
      <div className={styles['modal-footer']}>
        <Button icon='printer' text={getLabel(props.labels, 'print')} className={styles.print} onClick={handlePrint}></Button>
      </div>
    );
  }

  function getCADModalFooter() {
    return (
      <div className={styles['cad-footer']}>
        <Button
          className={styles.download}
          text={getLabel(props.labels, '3d_cad_tos_cta_download')}
          onClick={downloadCADDoc}
          disabled={!isCADDownloadEnabled}></Button>
        <Button
          text={getLabel(props.labels, '3d_cad_tos_cta_cancel')}
          onClick={() => setShowDocModal(undefined)}
          buttonType='text'></Button>
      </div>
    );
  }

  function getStatementOfConformityContent() {
    return (
      <>
        <p className={`body-text ${styles.text}`}>{getLabel(props.labels, 'CertificateContents')}</p>
        <table>
          <tbody>
            <tr>
              <td>{getLabel(props.labels, 'ROHScert_RS_StockNumber')}</td>
              <td>{props.titleArea?.formattedStockCode}</td>
            </tr>
            <tr>
              <td>{getLabel(props.labels, 'ROHScert_Product_Description')}</td>
              <td>{props.titleArea?.title}</td>
            </tr>
            <tr>
              <td>{getLabel(props.labels, 'ROHScert_Manufacturer_Brand')}</td>
              <td>{props.titleArea?.brand}</td>
            </tr>
            {props.titleArea?.mpn && (
              <tr>
                <td>{getLabel(props.labels, 'ROHScert_Manufacturer_s_part_number')}</td>
                <td>{props.titleArea?.mpn}</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className={styles.text}>
          <p>{getLabel(props.labels, 'CertificateForegoingInformation')}</p>
          <p>
            {getLabel(props.labels, 'ROHScert_Header_RSComponents').toUpperCase()}
            <br />
            {getLabel(props.labels, 'ROHScert_Date')} {moment().format('MMM DD, YYYY')}
          </p>
          <hr />
          <p>{getLabel(props.labels, 'ROHScert_Address')}</p>
        </div>
      </>
    );
  }

  function getROHSContent() {
    return (
      <div className={styles.text}>
        <img src={ROHSLogo} alt='rohs-logo' width={115} height={80} className={styles.logo}></img>
        <div className='body-text' dangerouslySetInnerHTML={{ __html: getLabel(props.labels, 'ROHScert_TopPart') }}></div>
        <div className={`${styles['product-details']} ${styles['page-break']}`}>
          <p className={styles['header']}>{getLabel(props.labels, 'ROHScert_Compliant_product_details')}</p>
          <div className={styles.details}>
            <div>{getLabel(props.labels, 'ROHScert_RS_StockNumber')}</div>
            <div>{props.titleArea?.formattedStockCode}</div>
            <div>{getLabel(props.labels, 'ROHScert_Product_Description')}</div>
            <div>{props.titleArea?.title}</div>
            <div>{getLabel(props.labels, 'ROHScert_Manufacturer_Brand')}</div>
            <div>{props.titleArea?.brand}</div>
            {props.titleArea?.mpn && (
              <>
                <div>{getLabel(props.labels, 'ROHScert_Manufacturer_s_part_number')}</div>
                <div>{props.titleArea?.mpn}</div>
              </>
            )}
          </div>
        </div>
        <hr />
        <p>
          {getLabel(props.labels, 'ROHScert_Header_RSComponents').toUpperCase()}
          <br />
          {getLabel(props.labels, 'ROHScert_Date')} {moment().format('MMM DD, YYYY')}
          <br />
          <br />
          {getLabel(props.labels, 'ROHScert_Address')}
        </p>
      </div>
    );
  }

  function downloadCADDoc() {
    if (props.cadDocuments && isCADDownloadEnabled) {
      setShowDocModal(undefined);
      documentClickEvent(DocumentType.TraceParts3DCad);
      let docId: number = selectedCADDocument ?? props.cadDocuments?.tracePartsData.documents[0].id;
      props.downloadCADDocument(docId);
    }
  }
};

export default TechnicalDocuments;
